import { useState, useEffect } from 'react';
import { useInput, useRecordContext, InputProps } from 'react-admin';
import { TextField, Box, Typography } from '@mui/material';

interface JsonFieldProps extends Partial<InputProps> {
  source: string;
  label: string;
}

/**
 * Custom input field for editing JSON data
 * Displays JSON in a pretty-printed format and validates on blur
 */
export const JsonField = ({ source, label }: JsonFieldProps) => {
  const record = useRecordContext();
  const { field } = useInput({ source });
  const [jsonString, setJsonString] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Initialize from record
  useEffect(() => {
    if (record?.[source]) {
      try {
        setJsonString(JSON.stringify(record[source], null, 2));
      } catch {
        setJsonString('{}');
      }
    } else {
      setJsonString('{}');
    }
  }, [record, source]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setJsonString(value);
    setError(null);
  };

  const handleBlur = () => {
    try {
      const parsed = JSON.parse(jsonString || '{}');
      field.onChange(parsed);
      setError(null);
      // Pretty print the JSON
      setJsonString(JSON.stringify(parsed, null, 2));
    } catch (err) {
      setError('Invalid JSON format');
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <TextField
        fullWidth
        multiline
        rows={6}
        label={label}
        value={jsonString}
        onChange={handleChange}
        onBlur={handleBlur}
        error={!!error}
        helperText={error || 'Enter valid JSON'}
        variant="outlined"
        sx={{
          '& textarea': {
            fontFamily: 'monospace',
            fontSize: '0.875rem',
          },
        }}
      />
      {error && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};
